import { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 15_000;
const SCROLL_IDLE_DELAY_MS = 2_000;
const AUTO_DISMISS_MS = 30_000;

/**
 * Polls /api/inventory/catalog/new-count every 15 s when feedMode === 'recents'.
 *
 * @param {object} opts
 * @param {string}   opts.feedMode       - Current feed mode ('nearby' | 'recents' | 'foryou')
 * @param {Function} opts.onLoadNewItems - Called when user taps the banner; should re-fetch feed
 *
 * @returns {{ newCount: number, loadNewItems: Function, resetCount: Function }}
 */
export function useNewItemsPolling({ feedMode, onLoadNewItems }) {
  const [newCount, setNewCount] = useState(0);

  // sinceRef tracks the timestamp of the last full feed load — NOT updated each poll.
  const sinceRef = useRef(new Date().toISOString());

  // Track scroll idle state via a ref (avoids stale closure issues in setInterval).
  const scrollIdleRef = useRef(true);
  const scrollTimerRef = useRef(null);

  // Auto-dismiss timer ref.
  const dismissTimerRef = useRef(null);

  // ── Scroll idle detection ──────────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      scrollIdleRef.current = false;
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        scrollIdleRef.current = true;
      }, SCROLL_IDLE_DELAY_MS);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimerRef.current);
    };
  }, []);

  // ── Auto-dismiss when banner is shown ─────────────────────────────────────
  useEffect(() => {
    if (newCount > 0) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(() => {
        setNewCount(0);
        // Reset since so subsequent polls start fresh from this point.
        sinceRef.current = new Date().toISOString();
      }, AUTO_DISMISS_MS);
    }
    return () => clearTimeout(dismissTimerRef.current);
  }, [newCount]);

  // ── Polling ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (feedMode !== 'recents') {
      setNewCount(0);
      return;
    }

    // Reset since when switching into recents mode.
    sinceRef.current = new Date().toISOString();
    setNewCount(0);

    const poll = async () => {
      // Don't poll when tab is hidden or user is actively scrolling.
      if (document.visibilityState === 'hidden') return;
      if (!scrollIdleRef.current) return;

      try {
        const url = `${API_URL}/api/inventory/catalog/new-count?since=${encodeURIComponent(sinceRef.current)}`;
        const res = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        if (data.count > 0) {
          setNewCount(data.count);
        }
      } catch {
        // Network error — silently skip, will retry next interval.
      }
    };

    const intervalId = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [feedMode]);

  // ── Public API ─────────────────────────────────────────────────────────────

  const loadNewItems = useCallback(() => {
    clearTimeout(dismissTimerRef.current);
    sinceRef.current = new Date().toISOString();
    setNewCount(0);
    onLoadNewItems();
  }, [onLoadNewItems]);

  const resetCount = useCallback(() => {
    clearTimeout(dismissTimerRef.current);
    setNewCount(0);
  }, []);

  return { newCount, loadNewItems, resetCount };
}
